const BASE = (window.ENV && window.ENV.API_BASE_URL) || '/v1';

// The session lives in an httpOnly cookie the API sets on login (PIN-320), so
// every request has to carry cookies; the API and the cabinet sit on different
// subdomains, which makes this a cross-origin request as far as fetch cares.
const withSession = (options = {}) => ({ ...options, credentials: 'include' });

// LEGACY_TOKEN_KEY held the JWT before the session moved to an httpOnly
// cookie (PIN-320). Every such token has long expired (24h), and sending one
// next to the cookie made the API answer 401 to everything (PIN-336), so it is
// only ever deleted now.
const LEGACY_TOKEN_KEY = 'jwt_token';

export function clearLegacyToken() { localStorage.removeItem(LEGACY_TOKEN_KEY); }

// A 401 means the session is gone (expired or signed out elsewhere). Drop it
// server-side and go to the login page once, instead of leaving a cabinet that
// looks signed in but fails every action.
let signingOut = false;
function onUnauthorized() {
  if (signingOut) return;
  signingOut = true;
  clearToken();
  fetch(`${BASE}/auth/logout`, withSession({ method: 'POST' }))
    .catch(() => {})
    .finally(() => {
      if (!window.location.pathname.startsWith('/login')) window.location.assign('/login');
    });
}

export async function authFetch(url, options = {}) {
  const res = await fetch(url, {
    ...withSession(options),
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (res.status === 401) onUnauthorized();
  return res;
}

// hasSession answers "is somebody signed in?" without touching the token: the
// API sets a secret-free marker cookie next to the httpOnly one for exactly
// this purpose.
export function hasSession() {
  return document.cookie.split('; ').some((c) => c.startsWith('sb_session='));
}

export function getRole()        { return localStorage.getItem('jwt_role'); }
export function setRole(r)       { localStorage.setItem('jwt_role', r); }
export function clearToken()     { clearLegacyToken(); localStorage.removeItem('jwt_role'); }

export const api = {
  // Auth (public — no JWT)
  login:    (email, password) =>
    fetch(`${BASE}/auth/login`,    withSession({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, password }) })),
  register: (name, email, password) =>
    fetch(`${BASE}/auth/register`, withSession({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name, email, password }) })),
  logout:   () => authFetch(`${BASE}/auth/logout`, { method: 'POST' }),
  // Email verification + password reset (public — no JWT)
  verifyEmail:        (token) => fetch(`${BASE}/auth/verify?token=${encodeURIComponent(token)}`),
  resendVerification: (email) => fetch(`${BASE}/auth/verify/resend`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }) }),
  forgotPassword:     (email) => fetch(`${BASE}/auth/password/forgot`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }) }),
  resetPassword:      (token, password) => fetch(`${BASE}/auth/password/reset`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token, password }) }),

  // Account
  getAccount:     ()     => authFetch(`${BASE}/account`),
  updateAccount:  (data) => authFetch(`${BASE}/account`, { method: 'PUT', body: JSON.stringify(data) }),
  changePassword: (current, newPwd) =>
    authFetch(`${BASE}/account/password`, { method: 'PUT', body: JSON.stringify({ current, new: newPwd }) }),

  // Projects
  listProjects:  ()           => authFetch(`${BASE}/projects`),
  createProject: (name)       => authFetch(`${BASE}/projects`, { method: 'POST', body: JSON.stringify({ ProjectName: name }) }),
  getProject:    (id)         => authFetch(`${BASE}/projects/${id}`),
  updateProject: (id, data)   => authFetch(`${BASE}/projects/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteProject: (id)         => authFetch(`${BASE}/projects/${id}`, { method: 'DELETE' }),

  // Checks (nested under project)
  listChecks:   (pid)            => authFetch(`${BASE}/projects/${pid}/checks`),
  createCheck:  (pid, data)      => authFetch(`${BASE}/projects/${pid}/checks`, { method: 'POST', body: JSON.stringify(data) }),
  updateCheck:  (pid, cid, data) => authFetch(`${BASE}/projects/${pid}/checks/${cid}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteCheck:  (pid, cid)       => authFetch(`${BASE}/projects/${pid}/checks/${cid}`, { method: 'DELETE' }),
  // Runs an AI-bot check once ("check now", PIN-328). With cid the stored bot
  // key is used when data carries none.
  testAIBot:    (pid, data, cid) => authFetch(`${BASE}/projects/${pid}/checks/ai-test${cid ? `?check_id=${cid}` : ''}`, { method: 'POST', body: JSON.stringify(data) }),
  // Historical availability report (uptime %, incidents, per-day series). 503 when
  // history is not configured on the server.
  getCheckReport: (pid, cid, days) => authFetch(`${BASE}/projects/${pid}/checks/${cid}/report?days=${days}`),

  // Notifications (nested under project)
  listNotifications:   (pid)            => authFetch(`${BASE}/projects/${pid}/notifications`),
  createNotification:  (pid, data)      => authFetch(`${BASE}/projects/${pid}/notifications`, { method: 'POST', body: JSON.stringify(data) }),
  updateNotification:  (pid, nid, data) => authFetch(`${BASE}/projects/${pid}/notifications/${nid}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteNotification:  (pid, nid)       => authFetch(`${BASE}/projects/${pid}/notifications/${nid}`, { method: 'DELETE' }),
  testNotification:    (pid, nid)       => authFetch(`${BASE}/projects/${pid}/notifications/${nid}/test`, { method: 'POST' }),

  // Telegram chat linking (nested under project)
  getTelegramLink:    (pid) => authFetch(`${BASE}/projects/${pid}/telegram/link`),
  startTelegramLink:  (pid) => authFetch(`${BASE}/projects/${pid}/telegram/link`, { method: 'POST' }),
  deleteTelegramLink: (pid) => authFetch(`${BASE}/projects/${pid}/telegram/link`, { method: 'DELETE' }),
};
